/**
 * Shopify adapter implementation of the CommerceClient interface
 * Uses composition pattern with specialized services
 */

import {
  IProductQueryReqest,
  ICollectionQueryRequest,
  ISingleProductRequest,
  ISingleCollectionRequest,
  ICartItemRequest,
  ICartUpdateRequest,
  IOptionsRequest,
  IResponse,
  IBlogArticleRequest,
  ITrendingArticleRequest,
  IArticleByHandleRequest,
  IGetArticleRequest,
  IPageRequest,
} from "../../models";
import { IShopifyConfig, IShopifyStorefrontClient } from "./interface";
import { createShopifyStorefrontClient } from "./client";
import { createDomainLogger } from "../../logger";
import { BaseCommerceClient } from "../../core/client";

// Import specialized services
import { ProductService } from "./product/service";
import { CollectionService } from "./collection/service";
import { CartService } from "./cart/service";
import { BlogService } from "./blog/service";
import { PageService } from "./page/service";
import { CustomerOrderService } from "./order/service";

/**
 * Shopify adapter implementation of the CommerceClient interface
 * Uses composition pattern with specialized services for better maintainability
 */
export class ShopifyCommerceClient extends BaseCommerceClient {
  protected readonly adapterName = "Shopify";
  private client: IShopifyStorefrontClient;
  private logger: ReturnType<typeof createDomainLogger>;

  // Specialized services
  private productService: ProductService;
  private collectionService: CollectionService;
  private cartService: CartService;
  private blogService: BlogService;
  private pageService: PageService;
  private customerOrderService: CustomerOrderService;

  constructor(config: IShopifyConfig) {
    super();
    this.client = createShopifyStorefrontClient(config);
    this.logger = createDomainLogger("shopify", "shopify");

    // Initialize specialized services
    this.productService = new ProductService(this.client, this.logger);
    this.collectionService = new CollectionService(this.client, this.logger);
    this.cartService = new CartService(this.client, this.logger);
    this.blogService = new BlogService(this.client, this.logger);
    this.pageService = new PageService(this.client, this.logger);
    this.customerOrderService = new CustomerOrderService(
      this.client,
      this.logger
    );
  }

  // ============================================================================
  // PRODUCT API METHODS - Delegated to ProductService
  // ============================================================================

  /**
   * Get products based on query parameters
   */
  async getProducts(
    params: IProductQueryReqest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.productService.getProducts(params, options);
  }

  /**
   * Get a product by ID
   */
  async getProduct(
    params: ISingleProductRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.productService.getProduct(params, options);
  }

  /**
   * Get multiple products by their handles using batch query
   */
  async getProductsByHandles(
    handles: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.productService.getProductsByHandles(handles, options);
  }

  // ============================================================================
  // COLLECTION API METHODS - Delegated to CollectionService
  // ============================================================================

  /**
   * Get collections based on query parameters
   */
  async getCollections(
    params: ICollectionQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.collectionService.getCollections(params, options);
  }

  /**
   * Get a collection by ID
   */
  async getCollection(
    params: ISingleCollectionRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.collectionService.getCollection(params, options);
  }

  // ============================================================================
  // CART API METHODS - Delegated to CartService
  // ============================================================================

  /**
   * Create a new cart
   */
  async createCart(options?: IOptionsRequest): Promise<IResponse> {
    return this.cartService.createCart(options);
  }

  /**
   * Get a cart by ID
   */
  async getCart(id: string, options?: IOptionsRequest): Promise<IResponse> {
    return this.cartService.getCart(id, options);
  }

  /**
   * Update a cart
   */
  async updateCart(
    id: string,
    data: ICartUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.updateCart(id, data, options);
  }

  /**
   * Add items to a cart
   */
  async addToCart(
    cartId: string,
    items: ICartItemRequest[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.addToCart(cartId, items, options);
  }

  /**
   * Remove items from a cart
   */
  async removeFromCart(
    cartId: string,
    itemIds: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.removeFromCart(cartId, itemIds, options);
  }

  // ============================================================================
  // BLOG API METHODS - Delegated to BlogService
  // ============================================================================

  /**
   * Get articles
   */
  async getArticle(
    params?: IGetArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.blogService.getArticle(params, options);
  }

  /**
   * Get blog articles
   */
  async getBlogArticles(
    params: IBlogArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.blogService.getBlogArticles(params, options);
  }

  /**
   * Get trending articles
   */
  async getTrendingArticles(
    params: ITrendingArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.blogService.getTrendingArticles(params, options);
  }

  /**
   * Get article by handle
   */
  async getArticleByHandle(
    params: IArticleByHandleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.blogService.getArticleByHandle(params, options);
  }

  // ============================================================================
  // PAGE API METHODS - Delegated to PageService
  // ============================================================================

  /**
   * Get page by handle
   */
  async getPageByHandle(
    params: IPageRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.pageService.getPageByHandle(params, options);
  }

  // ============================================================================
  // CUSTOMER & ORDER API METHODS - Delegated to CustomerOrderService
  // ============================================================================

  /**
   * Get order history for a customer
   */
  async getOrderHistory(
    customerAccessToken: string,
    page = 1,
    limit = 10,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.customerOrderService.getOrderHistory(
      customerAccessToken,
      page,
      limit,
      options
    );
  }

  /**
   * Get a single order by ID
   */
  async getOrderById(
    customerAccessToken: string,
    orderId: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.customerOrderService.getOrderById(
      customerAccessToken,
      orderId,
      options
    );
  }

  /**
   * Get customer profile
   */
  async getCustomerProfile(
    customerAccessToken: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.customerOrderService.getCustomerProfile(
      customerAccessToken,
      options
    );
  }
}
